import React, { useState, useRef, useEffect } from 'react';
import { jsPDF } from 'jspdf';

// The tutor's instructions and the four documents now live on the SERVER
// (see api/_tutorPrompt.js and api/_corpus.js). The browser only sends the
// conversation, so learners never see or alter the governance prompt.
const PROXY_URL = "/api/tutor";

function Tutor() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Welcome to the AI Tutor. I'm here to help you understand and apply the Navigational Mind Framework.\n\nYou might want to:\n• Explore a concept you're finding difficult\n• Apply the framework to your own experience\n• Work through a specific navigational challenge\n• Deepen your understanding of a particular resident or dynamic\n\nWhat would you like to explore?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput('');
    setIsLoading(true);

    try {
      // Build a clean conversation for the API: only real user/assistant
      // turns, and it must begin with a user turn — so drop the opening
      // welcome message (a UI-only assistant greeting).
      const apiMessages = nextMessages
        .filter((m, i) => !(i === 0 && m.role === 'assistant'))
        .map(m => ({ role: m.role, content: m.content }));

      const response = await fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: apiMessages })
      });

      if (!response.ok) {
        throw new Error('API request failed');
      }

      const data = await response.json();
      const text =
        data && data.content && data.content[0] && data.content[0].text
          ? data.content[0].text
          : "I'm sorry — I couldn't form a response just now. Please try again.";

      setMessages(prev => [...prev, { role: 'assistant', content: text }]);
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const downloadConversation = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    let yPosition = 20;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('AI Tutor Conversation', margin, yPosition);
    yPosition += 10;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Downloaded: ${new Date().toLocaleDateString()}`, margin, yPosition);
    yPosition += 15;

    messages.forEach((message) => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      const role = message.role === 'user' ? 'You' : 'AI Tutor';
      doc.text(`${role}:`, margin, yPosition);
      yPosition += 6;

      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(message.content, maxWidth);

      lines.forEach((line) => {
        if (yPosition > 280) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, margin, yPosition);
        yPosition += 5;
      });

      yPosition += 8;
    });

    doc.save('tutor-conversation.pdf');
  };

  return (
    <div className="tutor-page fade-in">
      <div className="tutor-intro">
        <h1>AI Tutor</h1>
        <p style={{ color: 'var(--color-text-secondary)' }}>
          Explore the framework, apply it to your experience, or work through a navigational challenge.
          The tutor guides through questions rather than providing answers, and quotes the foundational
          texts directly.
        </p>
      </div>

      <div className="chat-container">
        <div className="chat-messages">
          {messages.map((message, index) => (
            <div key={index} className={`chat-message ${message.role}`}>
              {message.content.split('\n').map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
          ))}
          {isLoading && (
            <div className="chat-message assistant">
              <p style={{ color: 'var(--color-text-muted)' }}>Thinking...</p>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-area">
          <textarea
            className="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            rows={2}
          />
          <button
            className="chat-send"
            onClick={sendMessage}
            disabled={isLoading || !input.trim()}
          >
            Send
          </button>
        </div>

        <div className="chat-actions">
          <button className="btn btn-secondary" onClick={downloadConversation}>
            Download Conversation
          </button>
        </div>
      </div>
    </div>
  );
}

export default Tutor;
